import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Download, Loader2, Pause, Play, PlayCircle, Search, XCircle } from 'lucide-react';
import { RunNtaDialog } from '../components/nta/RunNtaDialog';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { ntaService } from '../services/ntaService';
import { NewBadge } from '../components/NewBadge';
import {
  NTA_STATUS_LABEL, fmtMoney, toNum, type NtaResultRow, type NtaResultStatus, type NtaRun,
} from '../lib/nta';
import {
  NTA_FILTERS, ntaPortfolioSummary, ntaPortfolioXlsxRows, ntaSummaryXlsxRows, ntaTopSavings, ntaTopXlsxRows,
} from '../lib/ntaPortfolio';
import { exportMultiSheetXLSX } from '../utils/exportUtils';

// Network Tariff Analysis — portfolio view (spec 8.3): one row per NMI in a run,
// biggest saving first, so Choice Energy can work the largest savings first and
// see which sites could not be assessed and why. Run and filters live in the URL.
// The NTA Report strip counts clients with a saving and how far issued results
// have progressed (accepted, finalised); each count filters the list.

const STATUS_STYLE: Record<NtaResultStatus, string> = {
  queued: 'bg-gray-100 text-gray-700',
  running: 'bg-blue-50 text-blue-700',
  calculated: 'bg-green-50 text-green-700',
  no_saving: 'bg-gray-100 text-gray-700',
  cannot_assess: 'bg-amber-50 text-amber-800',
  no_data: 'bg-gray-100 text-gray-500',
  approved: 'bg-indigo-50 text-indigo-700',
  issued: 'bg-emerald-100 text-emerald-800',
  accepted: 'bg-teal-100 text-teal-800',
  finalised: 'bg-green-600 text-white',
  failed: 'bg-red-50 text-red-700',
};

const FILTERS = NTA_FILTERS;

export const NtaStatusBadge = ({ status }: { status: NtaResultStatus }) => (
  <span className={cn('rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap', STATUS_STYLE[status])}>
    {NTA_STATUS_LABEL[status]}
  </span>
);

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function downloadPortfolioCsv(rows: NtaResultRow[], name: string) {
  const header = ['Client', 'NMI', 'Market Region', 'Current Tariff', 'Best Eligible Tariff', 'Current 12m Cost',
    'Best 12m Cost', 'Annual Saving', 'Status', 'Current Tariff Not Viable', 'Reasons', 'Error'];
  const body = rows.map((r) => [
    r.site?.client?.name, r.nmi, r.market_region, r.baseline_code, r.best_code, r.baseline_total, r.best_total,
    r.savings, NTA_STATUS_LABEL[r.status], r.baseline_not_viable ? 'Y' : 'N', r.reasons.join('; '), r.error,
  ].map(csvCell).join(','));
  const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export const NtaPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [runs, setRuns] = useState<NtaRun[]>([]);
  const [results, setResults] = useState<NtaResultRow[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [acting, setActing] = useState(false);

  const runId = params.get('run') ?? runs[0]?.id ?? '';
  const filter = params.get('filter') ?? 'all';
  const q = params.get('q') ?? '';
  const run = runs.find((r) => r.id === runId) ?? null;

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingRuns(true);
      const res = await ntaService.listRuns();
      if (!mounted) return;
      if (res.success) setRuns(res.data ?? []);
      else setError(res.error || 'Failed to load NTA runs');
      setLoadingRuns(false);
    };
    void load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!runId) { setResults([]); return; }
    let mounted = true;
    const load = async () => {
      if (reloadTick === 0) setLoading(true);
      setError(null);
      const res = await ntaService.listResults(runId);
      if (!mounted) return;
      if (res.success) setResults(res.data ?? []);
      else setError(res.error || 'Failed to load NTA results');
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [runId, reloadTick]);

  // Drive a running engine run: one ~45 s server slice at a time while this page
  // is open. Claims are skip-locked, so two open tabs never double-process a site.
  const runStatus = run?.status;
  useEffect(() => {
    if (!runId || runStatus !== 'running' || run?.kind === 'import') return;
    let mounted = true;
    const drive = async () => {
      const res = await ntaService.work(runId);
      if (!mounted) return;
      if (!res.success) { setError(res.error || 'Run slice failed'); return; }
      const fresh = await ntaService.getRun(runId);
      if (!mounted || !fresh.success || !fresh.data) return;
      setRuns((prev) => prev.map((r) => (r.id === runId ? fresh.data! : r)));
      setReloadTick((t) => t + 1);
    };
    void drive();
    return () => { mounted = false; };
  }, [runId, runStatus, run?.kind, run?.done, run?.failed]);

  const act = async (fn: (id: string) => ReturnType<typeof ntaService.pause>) => {
    if (!run) return;
    setActing(true);
    const res = await fn(run.id);
    if (!res.success) setError(res.error || 'Action failed');
    const fresh = await ntaService.getRun(run.id);
    if (fresh.success && fresh.data) setRuns((prev) => prev.map((r) => (r.id === run.id ? fresh.data! : r)));
    setReloadTick((t) => t + 1);
    setActing(false);
  };

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, results.filter(f.match).length])),
    [results],
  );
  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0];
    const needle = q.trim().toLowerCase();
    return results.filter((r) => f.match(r) && (!needle
      || r.nmi.toLowerCase().includes(needle)
      || (r.site?.client?.name ?? '').toLowerCase().includes(needle)
      || (r.baseline_code ?? '').toLowerCase().includes(needle)));
  }, [results, filter, q]);

  const summary = useMemo(() => ntaPortfolioSummary(results), [results]);
  const top = useMemo(() => ntaTopSavings(results), [results]);
  const filterLabel = (FILTERS.find((x) => x.key === filter) ?? FILTERS[0]).label;

  const downloadXlsx = () => exportMultiSheetXLSX([
    { name: 'Results', rows: ntaPortfolioXlsxRows(shown) },
    { name: 'Summary', rows: ntaSummaryXlsxRows(summary, run, q ? `${filterLabel}, search "${q}"` : filterLabel, shown.length) },
    { name: 'Top 5', rows: ntaTopXlsxRows(top) },
  ], `NTA_portfolio_${run?.run_date ?? ''}`);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">Network Tariff Analysis <NewBadge /></h1>
          <p className="text-sm text-gray-500">
            Is each site on the cheapest network tariff it is eligible for? 12-month forward cost on the current tariff
            against every eligible alternative.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="nta-run-open">
          <PlayCircle className="h-4 w-4" /> Run NTA
        </Button>
      </div>
      <RunNtaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onStarted={(r) => { setRuns((prev) => [r, ...prev]); update({ run: r.id, filter: '', q: '' }); }}
      />

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
        <label className="text-xs text-gray-600">
          Run
          <select
            data-testid="nta-run-select"
            className="mt-1 block min-w-[22rem] rounded border px-2 py-1.5 text-sm"
            value={runId}
            disabled={loadingRuns || runs.length === 0}
            onChange={(e) => update({ run: e.target.value })}
          >
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label || r.kind} · {r.run_date} · {r.total} sites
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-600">
          Search
          <span className="mt-1 flex items-center gap-1 rounded border px-2 py-1.5">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              data-testid="nta-search"
              className="w-56 text-sm outline-none"
              value={q}
              onChange={(e) => update({ q: e.target.value })}
              placeholder="NMI, client or tariff"
            />
          </span>
        </label>
        <div className="ml-auto">
          <Button
            size="sm"
            variant="outline"
            disabled={shown.length === 0}
            onClick={() => downloadPortfolioCsv(shown, `NTA_portfolio_${run?.run_date ?? ''}`)}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            data-testid="nta-export-xlsx"
            disabled={results.length === 0}
            onClick={downloadXlsx}
          >
            <Download className="h-4 w-4" /> Export XLSX <NewBadge />
          </Button>
        </div>
      </div>

      {run && run.kind !== 'import' && (
        <div className="rounded-lg border bg-white px-4 py-3" data-testid="nta-run-progress">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 text-gray-700">
              {run.status === 'running' && <Loader2 className="h-4 w-4 animate-spin text-[#3790C5]" />}
              <strong className="capitalize">{run.status}</strong>
              · {run.done + run.failed} of {run.total} sites assessed{run.failed ? ` · ${run.failed} failed` : ''}
            </span>
            <span className="flex gap-2">
              {run.status === 'running' && (
                <Button size="sm" variant="outline" disabled={acting} onClick={() => void act(ntaService.pause)}>
                  <Pause className="h-4 w-4" /> Pause
                </Button>
              )}
              {run.status === 'paused' && (
                <Button size="sm" variant="outline" disabled={acting} onClick={() => void act(ntaService.resume)}>
                  <Play className="h-4 w-4" /> Resume
                </Button>
              )}
              {['running', 'paused', 'queued'].includes(run.status) && (
                <Button size="sm" variant="outline" disabled={acting}
                  onClick={() => { if (window.confirm('Cancel this run? Sites not yet assessed are marked as cancelled.')) void act(ntaService.cancel); }}>
                  <XCircle className="h-4 w-4" /> Cancel
                </Button>
              )}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded bg-gray-100">
            <div className="h-full bg-[#3790C5] transition-all" style={{ width: `${run.total ? (100 * (run.done + run.failed)) / run.total : 0}%` }} />
          </div>
          {run.status === 'running' && (
            <p className="mt-1 text-xs text-gray-500">Runs on the server while this page is open. Leaving pauses progress; come back to continue.</p>
          )}
        </div>
      )}

      {run?.kind === 'import' && (
        <div className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
          <AlertCircle className="h-4 w-4" />
          Imported from the legacy Energetica calculator — reference results, not calculated by this platform.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {!loadingRuns && runs.length === 0 && !error && (
        <div className="rounded-lg border bg-white p-8 text-center text-sm text-gray-500">No NTA runs yet.</div>
      )}

      {runId && (
        <>
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:col-span-2" data-testid="nta-summary">
              {([
                ['Clients with a saving', String(summary.clientsWithSaving), 'saving', `${summary.sitesWithSaving} sites`],
                ['Issued', String(summary.issued), 'issued', 'incl. accepted and finalised'],
                ['Accepted', String(summary.accepted), 'accepted', 'incl. finalised'],
                ['Finalised', String(summary.finalised), 'finalised', null],
                ['Cannot assess', String(summary.cannotAssess), 'cannot_assess', `of ${summary.sites} sites`],
                ['No data', String(summary.noData), 'no_data', null],
              ] as const).map(([label, value, key, hint]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`nta-summary-${key}`}
                  onClick={() => update({ filter: key })}
                  className={cn('rounded-lg border bg-white px-4 py-3 text-left hover:border-[#3790C5]',
                    filter === key && 'border-[#3790C5] ring-1 ring-[#3790C5]')}
                >
                  <div className="flex items-center gap-1 text-xs text-gray-500">{label}{(key === 'accepted' || key === 'finalised') && <NewBadge />}</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">{loading ? '…' : value}</div>
                  {hint && <div className="text-xs text-gray-400">{hint}</div>}
                </button>
              ))}
            </div>
            <div className="rounded-lg border bg-white" data-testid="nta-top5">
              <div className="flex items-center gap-2 border-b px-4 py-2 text-sm font-medium text-gray-900">Top 5 savings <NewBadge /></div>
              {!loading && top.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">No savings in this run.</p>}
              <ol className="divide-y divide-gray-100">
                {top.map((t, i) => (
                  <li key={t.id}>
                    <Link to={`/bill-validation/nta/${t.id}`} className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50">
                      <span className="w-4 text-xs text-gray-400">{i + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-gray-900">{t.client ?? '—'}</span>
                        <span className="block font-mono text-xs text-gray-500">{t.nmi}</span>
                      </span>
                      <span className="font-medium tabular-nums text-green-700">{fmtMoney(t.saving)}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="flex flex-wrap gap-2" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                data-testid={`nta-filter-${f.key}`}
                onClick={() => update({ filter: f.key === 'all' ? '' : f.key })}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs',
                  filter === f.key ? 'border-[#3790C5] bg-[#3790C5] text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
                )}
              >
                {f.label} <span className="opacity-70">{counts[f.key] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="overflow-x-auto rounded-lg border bg-white">
            <table data-testid="nta-table" className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">NMI</th>
                  <th className="px-4 py-2 font-medium">Region</th>
                  <th className="px-4 py-2 font-medium">Current tariff</th>
                  <th className="px-4 py-2 font-medium">Best eligible</th>
                  <th className="px-4 py-2 text-right font-medium">Current 12m</th>
                  <th className="px-4 py-2 text-right font-medium">Best 12m</th>
                  <th className="px-4 py-2 text-right font-medium">Annual saving</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">Loading results…</td></tr>
                )}
                {!loading && shown.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">No sites match.</td></tr>
                )}
                {!loading && shown.map((r) => {
                  const saving = toNum(r.savings);
                  const note = [...r.reasons, r.error].filter(Boolean).join('\n');
                  return (
                    <tr
                      key={r.id}
                      data-testid="nta-row"
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/bill-validation/nta/${r.id}`)}
                    >
                      <td className="px-4 py-2.5 text-gray-900">{r.site?.client?.name ?? '—'}</td>
                      <td className="px-4 py-2.5 font-mono">
                        <Link to={`/bill-validation/nta/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                          {r.nmi}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{r.market_region?.replace('AU-NEM.', '') ?? '—'}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap font-mono text-gray-700">
                        {r.baseline_code ?? '—'}
                        {r.baseline_not_viable && (
                          <span title="Current tariff is no longer eligible" className="ml-1 inline-flex align-middle text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-700">{r.best_code ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(r.baseline_total)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(r.best_total)}</td>
                      <td className={cn('px-4 py-2.5 text-right font-medium tabular-nums',
                        saving != null && saving > 0 ? 'text-green-700' : 'text-gray-500')}>
                        {saving != null && saving > 0 ? fmtMoney(saving) : '—'}
                      </td>
                      <td className="px-4 py-2.5" title={note || undefined}>
                        <NtaStatusBadge status={r.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
